import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Button,
    Drawer,
    Grid,
    Radio,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { batchPackagingApi, packagingElementsApi } from '../../../api/client';
import PackagingItemsTable, { EditablePackagingItem } from './PackagingItemsTable';
import type { BatchPackagingResponse } from '../../bdo/types';
import { formatGrams } from '../../bdo/types';

const { Text } = Typography;
const { useBreakpoint } = Grid;

function toEditable(items: any[]): EditablePackagingItem[] {
    return (items || []).map((item, index) => ({
        key: item.id || `item-${index}`,
        id: item.id,
        elementId: item.elementId,
        material: item.material,
        massGrams: Number(item.massGrams) || 0,
    }));
}

interface DrawerProps {
    batchId: string | null;
    open: boolean;
    onClose: () => void;
}

export function BatchPackagingDrawer({ batchId, open, onClose }: DrawerProps) {
    const queryClient = useQueryClient();
    const screens = useBreakpoint();
    const isMobile = screens.md === false;
    const [mode, setMode] = useState<'PRODUCT_DEFAULT' | 'CUSTOM'>('PRODUCT_DEFAULT');
    const [items, setItems] = useState<EditablePackagingItem[]>([]);

    const { data, isLoading } = useQuery<BatchPackagingResponse>({
        queryKey: ['batch-packaging', batchId],
        queryFn: () => batchPackagingApi.get(batchId!).then((res: any) => res.data),
        enabled: open && !!batchId,
    });

    const { data: elements = [] } = useQuery({
        queryKey: ['packaging-elements'],
        queryFn: () => packagingElementsApi.list().then((res: any) => res.data),
        enabled: open,
    });

    useEffect(() => {
        if (!data) return;
        setMode(data.mode);
        const source =
            data.mode === 'CUSTOM'
                ? data.customItems.length
                    ? data.customItems
                    : data.productDefaultItems
                : data.productDefaultItems;
        setItems(toEditable(source));
    }, [data]);

    const createElementMutation = useMutation({
        mutationFn: (name: string) => packagingElementsApi.create(name),
    });

    const handleCreateElement = async (name: string) => {
        const res = await createElementMutation.mutateAsync(name);
        queryClient.invalidateQueries({ queryKey: ['packaging-elements'] });
        return res.data;
    };

    const saveMutation = useMutation({
        mutationFn: () =>
            batchPackagingApi.update(batchId!, {
                mode,
                items:
                    mode === 'CUSTOM'
                        ? items
                              .filter((item) => item.elementId)
                              .map((item, index) => ({
                                  elementId: item.elementId,
                                  material: item.material,
                                  massGrams: Number(item.massGrams) || 0,
                                  sortOrder: index,
                              }))
                        : undefined,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batch-packaging', batchId] });
            queryClient.invalidateQueries({ queryKey: ['product-packaging'] });
            queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
            queryClient.invalidateQueries({ queryKey: ['bdo-sales'] });
            message.success('Kompozycja partii zapisana');
            onClose();
        },
        onError: (error: any) =>
            message.error(error?.response?.data?.message || 'Nie udało się zapisać'),
    });

    const totalGrams = useMemo(
        () => items.reduce((sum, item) => sum + (Number(item.massGrams) || 0), 0),
        [items],
    );

    const handleModeChange = (next: 'PRODUCT_DEFAULT' | 'CUSTOM') => {
        setMode(next);
        if (next === 'CUSTOM' && data) {
            const source = data.customItems.length
                ? data.customItems
                : data.productDefaultItems;
            setItems(toEditable(source));
        }
    };

    return (
        <Drawer
            title={
                data?.batch
                    ? `Opakowanie BDO — partia ${data.batch.lotNumber}`
                    : 'Opakowanie BDO partii'
            }
            open={open}
            onClose={onClose}
            width={isMobile ? '100%' : 760}
            destroyOnClose
            extra={
                <Button
                    type="primary"
                    onClick={() => saveMutation.mutate()}
                    loading={saveMutation.isPending}
                >
                    Zapisz
                </Button>
            }
        >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Radio.Group
                    value={mode}
                    onChange={(e) => handleModeChange(e.target.value)}
                >
                    <Radio.Button value="PRODUCT_DEFAULT">Domyślne z produktu</Radio.Button>
                    <Radio.Button value="CUSTOM">Własne</Radio.Button>
                </Radio.Group>

                {mode === 'PRODUCT_DEFAULT' ? (
                    <Alert
                        type="info"
                        showIcon
                        message="Partia używa aktualnej kompozycji produktu. Późniejsza zmiana produktu wpłynie na tę partię."
                    />
                ) : (
                    <>
                        {data && data.customItems.length === 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                message="Brak zapisanej własnej kompozycji — system użyje domyślnej kompozycji produktu. Zapisz, aby utworzyć kopię."
                            />
                        )}
                        <PackagingItemsTable
                            items={items}
                            elements={elements}
                            onChange={setItems}
                            onCreateElement={handleCreateElement}
                            isMobile={isMobile}
                        />
                    </>
                )}

                {mode === 'PRODUCT_DEFAULT' && (
                    <Table
                        dataSource={toEditable(data?.productDefaultItems || [])}
                        loading={isLoading}
                        rowKey="key"
                        size="small"
                        pagination={false}
                        scroll={{ x: 420 }}
                        columns={[
                            {
                                title: 'Element',
                                key: 'element',
                                render: (_: any, record: EditablePackagingItem) => {
                                    const element = (data?.productDefaultItems || []).find(
                                        (i) => i.id === record.id,
                                    );
                                    return element?.element?.name || '—';
                                },
                            },
                            {
                                title: 'Materiał',
                                dataIndex: 'material',
                                key: 'material',
                                render: (value: string) => <Tag>{value}</Tag>,
                            },
                            {
                                title: 'Masa [g/szt.]',
                                dataIndex: 'massGrams',
                                key: 'massGrams',
                                align: 'right' as const,
                                render: (value: number) => formatGrams(value),
                            },
                        ]}
                    />
                )}

                <Text strong>Razem: {formatGrams(totalGrams)} g/szt.</Text>
            </Space>
        </Drawer>
    );
}

interface SectionProps {
    batchId: string | null;
    disabled?: boolean;
}

/** Sekcja BDO w istniejącym modalu partii (Magazyn → Partie). */
export function BatchPackagingSection({ batchId, disabled }: SectionProps) {
    const queryClient = useQueryClient();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const { data, isLoading } = useQuery<BatchPackagingResponse>({
        queryKey: ['batch-packaging', batchId],
        queryFn: () => batchPackagingApi.get(batchId!).then((res: any) => res.data),
        enabled: !!batchId,
    });

    const modeMutation = useMutation({
        mutationFn: (mode: 'PRODUCT_DEFAULT' | 'CUSTOM') =>
            batchPackagingApi.update(batchId!, { mode }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['batch-packaging', batchId] });
            queryClient.invalidateQueries({ queryKey: ['bdo-summary'] });
            message.success('Tryb kompozycji partii zapisany');
        },
        onError: () => message.error('Nie udało się zapisać trybu kompozycji'),
    });

    if (!batchId) {
        return (
            <Alert
                type="info"
                showIcon
                message="Kompozycję BDO można ustawić po zapisaniu partii."
            />
        );
    }

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Text strong>Opakowanie BDO</Text>
            <Radio.Group
                value={data?.mode || 'PRODUCT_DEFAULT'}
                disabled={disabled || isLoading || modeMutation.isPending}
                onChange={(e) => {
                    const next = e.target.value;
                    if (next === 'CUSTOM') {
                        setDrawerOpen(true);
                    } else {
                        modeMutation.mutate('PRODUCT_DEFAULT');
                    }
                }}
            >
                <Radio value="PRODUCT_DEFAULT">Domyślne z produktu</Radio>
                <Radio value="CUSTOM">Własne</Radio>
            </Radio.Group>
            <Space wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    Masa/szt.: {formatGrams(data?.totals?.totalGrams || 0)} g
                    {data?.usesProductDefault ? ' (kompozycja produktu)' : ' (własna partii)'}
                </Text>
                <Button
                    size="small"
                    icon={<EditOutlined />}
                    disabled={disabled}
                    onClick={() => setDrawerOpen(true)}
                >
                    Edytuj składniki
                </Button>
            </Space>

            <BatchPackagingDrawer
                batchId={batchId}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            />
        </Space>
    );
}
